import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, ChevronRight, Wrench, User, Car } from "lucide-react";
import { api } from "@/lib/api";
import { STATUS_META, fmtTime, prettyDuration, toDate, normalizeStatus } from "@/pages/appointments/lib";

/**
 * Compact "Today's Appointments" strip for Owner + Advisor dashboards.
 * Deep-links into the Day view of the calendar.
 */
export function TodaysAppointmentsStrip() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const now = new Date();
    const startOfDay = new Date(now); startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(now); endOfDay.setHours(23, 59, 59, 999);
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}:00`;
    api.get("/appointments", { params: { from_date: fmt(startOfDay), to_date: fmt(endOfDay) } })
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const now = Date.now();
  const upcoming = rows
    .filter((r) => normalizeStatus(r.status) !== "cancelled")
    .sort((a, b) => (toDate(a.scheduled_at)?.getTime() || 0) - (toDate(b.scheduled_at)?.getTime() || 0));

  return (
    <section
      className="bg-white rounded-xl border border-[#E4E7EC] p-4"
      data-testid="dashboard-today-appointments"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-[#a8dadc]/30 text-[#457b9d] grid place-items-center">
            <Calendar size={14} />
          </div>
          <div>
            <h3 className="text-[14px] font-bold text-[#1d3557] leading-tight">Today&apos;s Appointments</h3>
            <p className="text-[11.5px] text-[#667085]">
              {loading ? "Loading…" : `${upcoming.length} slot${upcoming.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <Link
          to="/appointments"
          data-testid="dashboard-today-appointments-view-all"
          className="text-[11.5px] font-semibold text-[#457b9d] hover:text-[#e63946] inline-flex items-center gap-1"
        >
          View calendar <ChevronRight size={12} />
        </Link>
      </div>
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[1, 2, 3].map((k) => (
            <div key={k} className="h-14 rounded-lg bg-[#F7F9FC] animate-pulse" />
          ))}
        </div>
      ) : upcoming.length === 0 ? (
        <div className="rounded-lg bg-[#F1FAEE] px-3 py-3 text-[12.5px] text-[#667085] flex items-center justify-between">
          <span>No appointments today.</span>
          <Link
            to="/appointments"
            className="text-[11.5px] font-semibold text-[#e63946] hover:underline"
            data-testid="dashboard-today-appointments-book"
          >
            + Book one
          </Link>
        </div>
      ) : (
        <ul className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {upcoming.slice(0, 6).map((a) => {
            const key = normalizeStatus(a.status);
            const meta = STATUS_META[key] || STATUS_META.scheduled;
            const past = toDate(a.scheduled_at)?.getTime() < now && key === "scheduled";
            return (
              <li key={a.id}>
                <Link
                  to={`/appointments`}
                  className="block rounded-lg border border-[#E4E7EC] p-2.5 hover:border-[#a8dadc] hover:bg-[#F1FAEE] transition-colors"
                  data-testid={`dashboard-today-appt-${a.number}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-1.5 h-6 rounded-sm"
                      style={{ background: meta.event }}
                    />
                    <span className="text-[13px] font-bold text-[#1d3557] tabular-nums">
                      {fmtTime(a.scheduled_at)}
                    </span>
                    <span className="text-[10.5px] text-[#667085]">{prettyDuration(a.duration_min)}</span>
                    {past && (
                      <span className="ml-auto text-[9.5px] font-bold text-[#e63946] uppercase">Overdue</span>
                    )}
                  </div>
                  <div className="text-[12.5px] font-semibold text-[#1d3557] truncate">
                    {a.customer_snapshot?.name || "—"}
                  </div>
                  <div className="text-[10.5px] text-[#667085] truncate flex items-center gap-1">
                    <Car size={10} className="inline" />
                    {a.vehicle_snapshot?.reg_number || "no vehicle"}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export default TodaysAppointmentsStrip;
